import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Search, ArrowLeft, Clock, Phone, Filter, X, Store, ChevronRight } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { ScrollArea, ScrollBar } from "../components/ui/scroll-area";
import { Seo } from "../components/Seo";
import { isOpenNow } from "../lib/hours";
import { toast } from "sonner";
import { API } from "../App";

export default function DirectoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tenants, setTenants] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get("category") || "");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = {};
        if (searchQuery) params.search = searchQuery;
        if (selectedCategory) params.category = selectedCategory;
        
        const [tenantsRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/tenants`, { params }),
          axios.get(`${API}/categories`)
        ]);
        setTenants(tenantsRes.data);
        setCategories(categoriesRes.data.categories);
      } catch (e) {
        toast.error("Couldn't load stores. Please check your connection and try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [searchQuery, selectedCategory]);

  const handleCategoryClick = (category) => {
    if (selectedCategory === category) {
      setSelectedCategory("");
      searchParams.delete("category");
    } else {
      setSelectedCategory(category);
      searchParams.set("category", category);
    }
    setSearchParams(searchParams);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedCategory("");
    setSearchParams({});
  };

  return (
    <div className="min-h-screen bg-background">
      <Seo
        title="Store Directory"
        path="/directory"
        description="Browse all stores at Nyali Plaza, Mombasa. Search and filter shops by category — supermarket, fashion, electronics, pharmacy, jewellery and more, with contacts and opening hours."
      />
      {/* Header */}
      <header className="sticky top-0 z-50 glass glass-dark border-b border-border/50">
        <div className="max-w-md mx-auto md:max-w-4xl lg:max-w-6xl px-4 py-3">
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="back-button">
              <Button variant="ghost" size="icon" className="rounded-full">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                data-testid="directory-search-input"
                type="text"
                placeholder="Search stores..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  if (e.target.value) {
                    searchParams.set("search", e.target.value);
                  } else {
                    searchParams.delete("search");
                  }
                  setSearchParams(searchParams);
                }}
                className="h-10 pl-10 pr-4 rounded-full bg-muted border-0"
              />
            </div>
          </div>
        </div>
      </header>

      {/* Category Filter Pills */}
      <div className="sticky top-[57px] z-40 bg-background/95 backdrop-blur-sm border-b border-border/50">
        <div className="max-w-md mx-auto md:max-w-4xl lg:max-w-6xl px-4 py-3">
          <ScrollArea className="w-full whitespace-nowrap">
            <div className="flex items-center gap-2">
              <Button
                variant={selectedCategory === "" ? "default" : "outline"}
                size="sm"
                className="rounded-full shrink-0"
                onClick={() => handleCategoryClick("")}
                data-testid="filter-all"
              >
                All
              </Button>
              {categories.map((category) => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? "default" : "outline"}
                  size="sm"
                  className="rounded-full shrink-0"
                  onClick={() => handleCategoryClick(category)}
                  data-testid={`filter-${category.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {category}
                </Button>
              ))}
            </div>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        </div>
      </div>

      {/* Active Filters */}
      {(searchQuery || selectedCategory) && (
        <div className="max-w-md mx-auto md:max-w-4xl lg:max-w-6xl px-4 py-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-muted-foreground">Filters:</span>
            {searchQuery && (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm">
                "{searchQuery}"
                <button onClick={() => {
                  setSearchQuery("");
                  searchParams.delete("search");
                  setSearchParams(searchParams);
                }}>
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {selectedCategory && (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm">
                {selectedCategory}
                <button onClick={() => {
                  setSelectedCategory("");
                  searchParams.delete("category");
                  setSearchParams(searchParams);
                }}>
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            <button onClick={clearFilters} className="text-sm text-muted-foreground hover:text-foreground">
              Clear all
            </button>
          </div>
        </div>
      )}

      {/* Results */}
      <main className="max-w-md mx-auto md:max-w-4xl lg:max-w-6xl px-4 py-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg font-semibold text-foreground">
            {loading ? "Loading..." : `${tenants.length} Store${tenants.length !== 1 ? 's' : ''}`}
          </h2>
        </div>

        {loading ? (
          <div className="space-y-4">
            {Array(5).fill(0).map((_, i) => (
              <div key={i} className="bg-card rounded-2xl h-24 animate-pulse border border-border/50" />
            ))}
          </div>
        ) : tenants.length === 0 ? (
          <div className="text-center py-12">
            <Store className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-heading font-semibold text-foreground mb-2">No stores found</h3>
            <p className="text-muted-foreground text-sm mb-4">Try adjusting your search or filters</p>
            <Button variant="outline" onClick={clearFilters}>Clear Filters</Button>
          </div>
        ) : (
          <div className="space-y-3">
            {tenants.map((tenant, index) => (
              <motion.div
                key={tenant.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03, duration: 0.3 }}
              >
                <Link to={`/tenant/${tenant.slug || tenant.id}`} data-testid={`directory-tenant-${tenant.id}`}>
                  <div className="bg-card rounded-2xl border border-border/50 overflow-hidden hover:border-primary/30 hover:shadow-card-hover transition-all group p-4 flex gap-4">
                    <div className="w-20 h-20 md:w-24 md:h-24 rounded-xl overflow-hidden shrink-0">
                      <img
                        src={tenant.image_url || "https://images.unsplash.com/photo-1635930376155-4ee50db13cca?w=800"}
                        alt={tenant.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h3 className="font-heading font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-1">
                            {tenant.name}
                            {tenant.is_anchor && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full bg-secondary/20 text-secondary-foreground text-[10px] font-bold uppercase">
                                Anchor
                              </span>
                            )}
                          </h3>
                          <p className="text-sm text-muted-foreground">{tenant.category}</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0 group-hover:text-primary transition-colors" />
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${isOpenNow(tenant.business_hours) ? 'badge-open' : 'badge-closed'}`}>
                          <Clock className="w-2.5 h-2.5" />
                          {isOpenNow(tenant.business_hours) ? 'Open' : 'Closed'}
                        </span>
                        {tenant.contacts?.[0]?.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {tenant.contacts[0].phone}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
